
import { Component, Vue, Watch, Prop, Mixins } from "vue-property-decorator";
import { namespace, Getter, State } from "vuex-class";
import { ServerEventType, Group } from "@/store/eventqueue/types";
import { GPSService, GeoCodeResponse } from "@/services/gps.service";
import Multiselect from "vue-multiselect";
import api from "@/services/api.service";
import { validationMixin } from "vuelidate";
import { required, requiredIf, maxLength } from "vuelidate/lib/validators";
import SVButton from "@/components/form/SVButton.vue";

import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { ManualRaiseDefaultValues, ManualRaiseResult } from "../store/manual-raise/types";
import { UserPermissions, Region } from "../store/types";

import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";
import { AreaNode } from "@/types/groups/AreaNode";
import { groupsApi } from "@/services/api.groups.service";

import ManualRaiseTourSelect from "@/components/guard-tours/ManualRaiseTourSelect.vue";
import mobileApi from "@/services/api.mobile.service";
import { GuardTour } from "@/store/guard-tours/types";
import { DispatchRequest, MapBounds } from "@/store/mobile/types";
import { MapLayerData } from "@/store/map-layers/types";

import vselect from "vselect3";
import RegionSelect from "@/components/form/RegionSelect.vue";
import RegionalHandlerMixin from "@/mixins/RegionalHandlerMixin";

const Eventqueue = namespace("eventqueue");
const MapLayers = namespace("mapLayers");
const ManualRaiseStore = namespace("manualRaise");
const Regions = namespace("regions");

const MaxNotesLength = 1000;
const MaxRaisedByLength = 50;

@Component({
	mixins: [validationMixin],
	validations: {
		selectedGroup: {
			required: requiredIf(function() {
				return !this.selectedLayer;
			})
		},
		selectedLayer: {
			required: requiredIf(function() {
				return !this.selectedGroup;
			})
		},
		manualRaiseMarkerPosition: {
			required: requiredIf(function() {
				return this.pinNeedsDrop();
			})
		},
		selectedEventType: {
			eventNum: {
				required: requiredIf(function() {
					return !this.selectedTour;
				})
			}
		},
		selectedTour: {
			guardTourId: {
				required: requiredIf(function() {
					return !this.selectedEventType;
				})
			}
		},
		notes: {
			maxLength: maxLength(MaxNotesLength)
		},
		raisedBy: {
			maxLength: maxLength(MaxRaisedByLength)
		},
		canManualRaise: {
			required: requiredIf(function() {
				return !this.canManualRaiseArea;
			})
		}
	},
	components: {
		multiselect: Multiselect,
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		SVButton: SVButton,
		"area-tree-select": AreaTreeSelect,
		"manual-raise-tour-select": ManualRaiseTourSelect,
		"region-select": RegionSelect,
		"v-select": vselect
	}
})
export default class ManualRaise extends Mixins(RegionalHandlerMixin) {
	@Getter private getRequireMapPin: boolean;
	@Getter("getPermissions") private permissions: UserPermissions;
	@Getter private getFeature: (featureNames: string[]) => boolean;
	@Getter("getMapKey") private mapKey: string;

	@Regions.Getter private hasRegionsAvailable: boolean;
	@Regions.Getter private getRegionUrl: (regionId: number) => string;

	// Manual raise store
	@ManualRaiseStore.State("defaultValues") manualRaiseDefaults: ManualRaiseDefaultValues;
	@ManualRaiseStore.State manualRaiseShown: boolean;
	@ManualRaiseStore.State selectedRegion: Region;

	@ManualRaiseStore.Getter("getSelectedRegionId") selectedRegionId: number;

	@ManualRaiseStore.Mutation setManualRaiseShown: (visible: boolean) => void;
	@ManualRaiseStore.Mutation("setAreaMapBounds") setManualRaiseAreaMapBounds: (bounds) => void;
	@ManualRaiseStore.Mutation("setAreaLatLngString") setManualRaiseAreaLatLngString: (latLngString: string) => void;
	@ManualRaiseStore.Mutation setDefaultValues: (defaults: ManualRaiseDefaultValues) => void;
	@ManualRaiseStore.Mutation setSelectedRegion: (region: Region) => void;
	@ManualRaiseStore.Mutation setOnCompleteHandler: (handler: (result: ManualRaiseResult) => void) => void;
	@ManualRaiseStore.Mutation resetState: () => void;

	@ManualRaiseStore.Action raiseManualAlarm: ({ payload, autoHandle }) => Promise<void>;

	// Event queue
	@Eventqueue.Getter("serverEventTypes") eventTypes: ServerEventType[];
	@Eventqueue.Getter("getProcessingError") error: string;

	@Eventqueue.Action loadServerEventTypes: ({ serverType, groupID, regionId }) => Promise<void>;

	// Map layers
	@MapLayers.Action loadAreaMapLayersBounds: ({ groupId, regionId }) => Promise<MapBounds>;
	@MapLayers.Action loadMapLayersByGroupID: ({ groupId, regionId }) => Promise<MapLayerData>;

	@MapLayers.Mutation setManualRaiseMarkerPosition: any;
	@MapLayers.Mutation setManualRaiseLayers: any;
	@MapLayers.Mutation setSelectedManualRaiseLayer: any;
	@MapLayers.Mutation setMapLayersForGroup: (mapLayers: MapLayerData[]) => void;

	@MapLayers.Getter("getManualRaiseMarkerPosition") manualRaiseMarkerPosition: any;
	@MapLayers.Getter("getMapLayersForGroup") groupLayers!: MapLayerData[];
	@MapLayers.Getter getManualRaiseSelectedLayer: MapLayerData;
	@MapLayers.Getter("getSelectedMapElevation") selectedMapElevation: number;

	@Prop({ default: false }) public disableStyles: boolean;
	@Prop({ default: false }) public disableAutomaticClaim: boolean;
	@Prop({ default: false }) public disableMapAlert: boolean;
	@Prop({ default: false }) public isMapEnabled: boolean;
	@Prop({ default: false }) public allowRegionalRaise: boolean;

	public readonly maxNotesLength = MaxNotesLength;
	public readonly maxRaisedByLength = MaxRaisedByLength;

	// Payload properties
	public raisedBy: string = "";
	public notes: any = "";
	public selectedEventType: any = null;
	public selectedGroup: AreaNode = null;
	public autoHandle: boolean = true;
	public isRaising: boolean = false;
	public selectedTour: any = null;

	public settings: any = {};
	public groupSearchLoading = false;
	public selectedLayer: any = null;
	private showAutomaticClaim: boolean = false;
	private canManualRaiseArea: boolean = true;

	private get getDisableFlagIfEnabled(): string {
		if (this.getFeature(["Alarms", "EventQueue", "ManualRaise", "DisableOnArea"])) return "canManualRaise";
		return null;
	}

	private get isAssessmentTourEnabled(): boolean {
		return this.getFeature(["Mobile", "AssessmentTours"]);
	}

	public get selectedGroupLayer() {
		return this.getManualRaiseSelectedLayer;
	}

	public set selectedGroupLayer(value: MapLayerData) {
		this.setSelectedManualRaiseLayer(value);
	}

	public get raiseTitle() {
		if (this.pinNeedsDrop()) {
			return "You must drop a pin on the map";
		}

		if (this.selectedGroup == null && this.selectedLayer == null) return "You must select an area";

		if (this.selectedEventType == null || this.selectedEventType.eventNum == null)
			return "You must select an event type";

		return "Raise alarm";
	}

	private get showRegionSelect() {
		return this.hasRegionsAvailable && this.allowRegionalRaise && this.permissions.canManualRaiseRegionally;
	}

	private pinNeedsDrop() {
		return this.isMapEnabled && !this.manualRaiseMarkerPosition && this.getRequireMapPin;
	}

	@Watch("selectedRegion")
	public onSelectedRegionChanged() {
		this.selectedGroup = null;
		this.selectedGroupLayer = null;

		this.setManualRaiseAreaMapBounds(null);
		this.setManualRaiseMarkerPosition(null);
		this.setManualRaiseLayers([]);
		this.setMapLayersForGroup([]);
	}

	@Watch("selectedMapElevation")
	public onSelectedMapElevationChanged(value: any, oldValue: any) {
		let existingLayer = this.groupLayers.find(layer => layer.minElevation == value);

		if (existingLayer) {
			this.selectedGroupLayer = existingLayer;
		}
	}

	/**
	 * Watcher that fires when the visibility of the modal is altered.
	 * Applies any default values set in the overrides.
	 */
	@Watch("manualRaiseShown")
	public async onManualRaiseShownChanged(modalOpen: boolean): Promise<void> {
		if (!modalOpen) {
			this.clear();
			return;
		}

		// Turn off auto handle if we've disabled it
		this.autoHandle = !this.disableAutomaticClaim;

		if (this.manualRaiseDefaults) {
			this.raisedBy = this.manualRaiseDefaults.raisedByOverride || "";
			this.notes = this.manualRaiseDefaults.noteOverride || "";

			// If a default group is specified, load and set it.
			if (this.manualRaiseDefaults.selectedGroupOverride) {
				// Load the selected group.
				let newSelectedGroup = await groupsApi.getGroupTreeNode(
					this.manualRaiseDefaults.selectedGroupOverride,
					["Any"],
					false
				);

				if (newSelectedGroup) {
					this.selectedGroup = newSelectedGroup;
				}
			}
		}

		// We have opened the modal, lets get our event types.
		await this.fetchServerTypeEvents();
	}

	public async mounted() {
		this.setManualRaiseAreaMapBounds(null);
		this.setManualRaiseMarkerPosition(null);
		this.setManualRaiseLayers([]);

		this.$set(this, "selectedGroup", null);
		this.$set(this, "selectedGroupLayer", null);
		this.$set(this, "selectedLayer", null);

		// Turn off auto handle if we've disabled it
		if (this.disableAutomaticClaim) {
			this.autoHandle = false;
		}

		this.showAutomaticClaim = !this.disableAutomaticClaim;
	}

	@Watch("selectedGroup")
	private async onSelectedGroupChanged(area: AreaNode) {
		if (!area) {
			return;
		}

		await this.SetCanManualRaiseArea(area.id);
		await this.areaSelected(area);
	}

	public async areaSelected(area: AreaNode) {
		// Load event types for the new group.
		await this.fetchServerTypeEvents();

		// Reset lat long back to null so that to trigger watcher if same area selected
		this.setManualRaiseAreaLatLngString(null);

		const bounds = await this.loadAreaMapLayersBounds({ groupId: area.id, regionId: this.selectedRegionId });

		if (!bounds) {
			// if no bounds returned but the group has a lat long, use that
			if (area.latLong && area.latLong !== "") {
				this.setManualRaiseAreaLatLngString(area.latLong);
			} else if (area.address) {
				// if the group doesnt have a lat long - look up the bounds from the address.
				const geoCoder = new GPSService();

				try {
					const geoCodeResponse = await geoCoder.GeoCodeLookup(area.address, this.mapKey);
					if (geoCodeResponse.results && geoCodeResponse.results.length > 0) {
						const addressGeometry = geoCodeResponse.results[0].geometry;

						if (addressGeometry.viewport) {
							this.setManualRaiseAreaMapBounds({
								north: addressGeometry.viewport.northeast.lat,
								east: addressGeometry.viewport.northeast.lng,
								south: addressGeometry.viewport.southwest.lat,
								west: addressGeometry.viewport.southwest.lng
							});
						} else if (addressGeometry.location) {
							this.setManualRaiseAreaMapBounds({
								north: addressGeometry.location.lat,
								east: addressGeometry.location.lng,
								south: addressGeometry.location.lat,
								west: addressGeometry.location.lng
							});
						}
					}
				} catch (err) {
					this.$notify({
						type: "error",
						title: "Manual Raise - Address Lookup",
						text: "Unable to lookup the address you have entered, please ensure the address is valid."
					});
				}
			}
		} else if (bounds !== null) {
			this.setManualRaiseAreaMapBounds(bounds);
		}

		await this.loadMapLayersByGroupID({ groupId: area.id, regionId: this.selectedRegionId });

		if (this.groupLayers.length == null || this.groupLayers.length > 0) {
			this.$set(this, "selectedGroupLayer", this.groupLayers[0]);
		}
	}

	public async SetCanManualRaiseArea(areaId: number): Promise<void> {
		let group: Group = await api.loadGroup(areaId, this.selectedRegion?.url);
		if (!group || group.canManualRaise == null) {
			this.canManualRaiseArea = true;
		} else {
			this.canManualRaiseArea = group.canManualRaise;
		}
	}

	public clear() {
		this.$set(this, "selectedLayer", null);
		this.$set(this, "selectedGroup", null);
		this.$set(this, "selectedGroupLayer", null);
		this.$set(this, "selectedEventType", null);
		this.$set(this, "selectedTour", null);
		this.$set(this, "notes", "");
		this.$set(this, "raisedBy", "");
		this.setMapLayersForGroup([]);
		this.resetState();
	}

	public async raise() {
		this.$v.$touch();

		if (this.$v.$error) {
			return;
		}

		if (!this.isRaising) {
			this.isRaising = true;

			if (this.selectedEventType) {
				const location = this.manualRaiseMarkerPosition;
				const selectedLayerInfo =
					this.selectedLayer == null ? "" : `, MapLayerID: ${this.selectedLayer.mapLayerID}`;
				const alarmLocation = location ? location + selectedLayerInfo : "";
				const groupID = this.selectedLayer == null ? this.selectedGroup.id : this.selectedLayer.groupID;

				const payload = {
					mapLayerId: this.selectedLayer
						? this.selectedLayer.mapLayerID
						: this.selectedGroupLayer
						? this.selectedGroupLayer.mapLayerID
						: null,
					serverTypeEvent: this.selectedEventType.eventNum,
					notes: this.notes,
					raisedBy: this.raisedBy,
					location: alarmLocation,
					groupID: groupID,
					regionId: this.selectedRegionId
				};

				// Only auto-handle if the raise is happening in the local region. If we're doing a remote manual raise,
				// we'll handle the alarm after we've raised it through RegionalHandlerMixin.handleEventQueueItem.
				const autoHandle = this.selectedRegionId > 0 ? false : this.autoHandle;

				if (this.allowRegionalRaise && this.selectedRegionId > 0 && this.autoHandle) {
					// If we're performing a manual raise in a remote region, and auto-handle is set to true, attach a
					// post-raise hook to pick it up.
					this.setOnCompleteHandler(result => {
						// Get the URL of the region.
						const regionUrl = this.getRegionUrl(this.selectedRegionId);

						// Handle the event through the mixin.
						this.handleEventQueueItem(
							{
								eventID: result.eventID,
								regionID: this.selectedRegionId,
								regionURL: regionUrl
							},
							false
						);
					});
				}

				// Perform the manual raise.
				await this.raiseManualAlarm({
					payload,
					autoHandle
				});
			} else if (this.selectedTour) {
				const dispatchRequest: Partial<DispatchRequest> = {
					guardTourId: this.selectedTour.guardTourId,
					requester: this.raisedBy,
					requesterDescription: this.notes,
					dispatchType: "Assessment"
				};

				mobileApi.dispatchGuardToTour(dispatchRequest);
			}

			this.isRaising = false;
			this.setManualRaiseShown(false);
			this.clear();
		}
	}

	public async cancel() {
		this.setManualRaiseShown(false);
		this.clear();
		this.setManualRaiseAreaMapBounds(null);
	}

	public get noteRequiredForManualRaise() {
		return this.getFeature(["Alarms", "EventQueue", "ManualRaise", "RequireNote"]);
	}

	public get noteNotEntered() {
		if (!this.noteRequiredForManualRaise) {
			return false;
		}

		return this.notes.length == 0;
	}

	public serverTypeEventSelected(serverTypeEvent: any) {
		// Event handler for when the server type event has been selected
		this.selectedTour = null;
		this.selectedEventType = serverTypeEvent;
		this.showAutomaticClaim = true;
	}

	public tourSelected(assessmentTour: GuardTour) {
		// Event handler for when an assessment tour has been selected
		this.selectedEventType = null;
		this.selectedTour = assessmentTour;
		this.autoHandle = false;
		this.showAutomaticClaim = false;
	}

	public get selectedGroupId() {
		return this.selectedGroup == null ? 0 : this.selectedGroup.id;
	}

	private async fetchServerTypeEvents(): Promise<void> {
		await this.loadServerEventTypes({
			serverType: 435,
			groupID: this.selectedGroup?.id,
			regionId: this.selectedRegionId
		});
	}
}
